/**
 * Spec helpers for the UI: the bounds it should offer, and the arithmetic for
 * turning a slider into sixteenths and back.
 *
 * The bounds mirror `Limits` in `engine/include/planforge/validate.hpp`. They are
 * duplicated deliberately and they are *advice*: the engine remains the authority
 * and will still reject or clamp anything out of range. Their job is to keep the
 * controls inside the buildable envelope so a shopper meets a warning rather than
 * an error, not to enforce anything.
 */

import {
  DEFAULT_GROUND_SNOW_LOAD,
  SIXTEENTHS_PER_FOOT,
  SIXTEENTHS_PER_INCH,
  SPEC_VERSION,
  feet,
  inches,
  type Sixteenths,
  type Spec,
  type Stock,
} from "@planforge/wasm";

/** A raw number as a length. For arithmetic on values that are already sixteenths. */
export function sixteenths(value: number): Sixteenths {
  return Math.round(value) as Sixteenths;
}

export function toInches(value: Sixteenths): number {
  return value / SIXTEENTHS_PER_INCH;
}

export function toFeet(value: Sixteenths): number {
  return value / SIXTEENTHS_PER_FOOT;
}

/** `7' 6"`, the way a tape measure reads. Display only — the engine formats plans. */
export function formatFeetInches(value: Sixteenths): string {
  const totalInches = value / SIXTEENTHS_PER_INCH;
  const wholeFeet = Math.floor(totalInches / 12);
  const remainder = Math.round((totalInches - wholeFeet * 12) * 100) / 100;
  return remainder === 0 ? `${wholeFeet}'` : `${wholeFeet}' ${remainder}"`;
}

/** Mirrors `planforge::Limits`. Advice for the controls, not a second validator. */
export const LIMITS = {
  minFootprint: feet(4),
  maxFootprint: feet(24),
  minWallHeight: feet(6),
  maxWallHeight: feet(12),
  maxOverhang: inches(24),
  minOpeningSize: inches(12),
  /** The widest rough opening the engine frames: a ten-foot door. Mirrors `maxOpeningWidth`. */
  maxOpeningWidth: feet(10),
  /** Wall two neighboring openings must leave between them. */
  openingGap: inches(6),
  minPitchRise: 1,
  maxPitchRise: 12,
} as const;

/**
 * Dressed thicknesses, so the UI can measure a wall without asking the engine.
 *
 * The same fiction the trade uses: a 2x6 is 5 1/2". Only the two the engine will
 * frame a wall out of are here, because that is the only place the app needs it.
 */
const WALL_THICKNESS: Record<string, Sixteenths> = {
  "2x4": inches(3.5),
  "2x6": inches(5.5),
};

/** How thick a wall built to this spec is. Mirrors `planforge::wallThickness`. */
export function wallThickness(spec: Spec): Sixteenths {
  return WALL_THICKNESS[spec.framing.wallStock] ?? inches(3.5);
}

/**
 * How much wall an opening must leave at each end: the corner framing plus a king and
 * a jack. It follows the wall stock — 6 1/2" in a 2x4 wall, 8 1/2" in a 2x6 — so the
 * slider's range has to follow it too, or a 2x6 shed offers offsets the engine then
 * refuses. Mirrors `planforge::openingEdgeClearance`.
 */
export function openingEdgeClearance(spec: Spec): Sixteenths {
  return (wallThickness(spec) + inches(3)) as Sixteenths;
}

/** The wall framing a customer may choose. Mirrors the engine's own short list. */
export const WALL_STOCKS: readonly Stock[] = ["2x4", "2x6"];

/**
 * The floors a customer may put under the span table's answer.
 *
 * The lightest entry in each list is the engine's default, and it is the same as no
 * floor at all — the span table's own answer is never below it.
 */
export const JOIST_STOCKS: readonly Stock[] = ["2x6", "2x8", "2x10"];
export const RAFTER_STOCKS: readonly Stock[] = ["2x4", "2x6", "2x8", "2x10"];

/** The spacings the engine's span tables are built around. */
export const SPACINGS: readonly Sixteenths[] = [inches(12), inches(16), inches(24)];

/**
 * The board lengths a yard sells, which are the only haul lengths the engine accepts
 * (`haul_length_not_stocked`), and the one every plan before spec v8 was cut for.
 */
export const HAUL_LENGTHS: readonly Sixteenths[] = [8, 10, 12, 14, 16, 20].map((n) => feet(n));
export const DEFAULT_HAUL_LENGTH: Sixteenths = feet(12);

/** The design a first-time visitor lands on: a plain 10x12 with a door. */
export function defaultSpec(): Spec {
  return {
    kind: "shed",
    version: SPEC_VERSION,
    footprint: { width: feet(10), length: feet(12) },
    wallHeight: inches(90),
    roof: {
      style: "gable",
      support: "tied",
      pitch: { rise: 5, run: 12 },
      eaveOverhang: inches(12),
      rakeOverhang: inches(8),
      rafterSpacing: inches(24),
      gableVent: "none",
    },
    studSpacing: inches(16),
    joistSpacing: inches(16),
    framing: {
      wallStock: "2x4",
      minJoistStock: "2x6",
      minRafterStock: "2x4",
    },
    foundation: "skids",
    siding: "t111",
    haulLength: DEFAULT_HAUL_LENGTH,
    groundSnowLoad: DEFAULT_GROUND_SNOW_LOAD,
    openings: [
      {
        id: "door-1",
        wall: "front",
        type: "door",
        width: inches(38),
        height: inches(80),
        sill: inches(0),
        offset: inches(41),
      },
      {
        id: "window-1",
        wall: "right",
        type: "window",
        width: inches(36),
        height: inches(30),
        sill: inches(44),
        offset: inches(48),
      },
    ],
  };
}

/** How far along its wall an opening may sit, given everything else on that wall. */
export function offsetRange(spec: Spec, id: string): { min: number; max: number } {
  const opening = spec.openings.find((candidate) => candidate.id === id);
  if (opening === undefined) {
    return { min: 0, max: 0 };
  }
  const gableEnd = opening.wall === "front" || opening.wall === "back";
  const wallRun = gableEnd
    ? spec.footprint.width
    : spec.footprint.length - wallThickness(spec) * 2;
  const clearance = openingEdgeClearance(spec);

  return {
    min: clearance,
    max: Math.max(clearance, wallRun - opening.width - clearance),
  };
}
